import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import { promotionService } from "../../services/promotionService";
import { whatsappForPromotion } from "../../lib/whatsapp";
import { useBrand } from "../../context/SiteSettingsContext";
import { motion } from "framer-motion";
import { fadeUp, viewport } from "../../lib/motion";

export default function PromotionsStrip({ limit = 3 }) {
  const BRAND = useBrand();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    promotionService.active().then((list) => {
      setItems(list.slice(0, limit));
      setLoading(false);
    });
  }, [limit]);

  return (
    <section
      className="bg-ink py-24 md:py-32 border-t border-charcoal/50"
      data-testid="promotions-strip"
    >
      <div className="container-emilio">
        <div className="mb-12">
          <Eyebrow>Latest Promotions</Eyebrow>
          <RevealText
            as="h2"
            text={"Cleaning promotions,\nlive this season."}
            className="mt-4 font-display font-extrabold text-white text-display"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aspect-[4/5] bg-charcoal/40 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-charcoal">
            <p className="text-mutedtext max-w-md mx-auto">
              Contact us on WhatsApp to check our latest cleaning promotions.
            </p>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-emerald font-semibold"
            >
              Chat on WhatsApp <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {items.map((p) => (
              <motion.a
                key={p.id}
                href={whatsappForPromotion(p.title, p.whatsappMessage)}
                target="_blank"
                rel="noopener noreferrer"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                data-testid={`promotion-${p.slug}`}
                className="group border border-charcoal hover:border-emerald/60 transition-colors bg-charcoal/10 overflow-hidden block"
              >
                <div className="relative aspect-[5/6] overflow-hidden">
                  <img
                    src={p.imageUrl}
                    alt={p.altText || p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                  <div className="absolute top-4 left-4 bg-emerald text-ink text-eyebrow font-display font-bold uppercase tracking-[0.22em] px-3 py-1.5">
                    Promotion
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-white text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-mutedtext text-sm">{p.description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-emerald text-sm font-semibold">
                    {p.ctaLabel || "Ask About This Promotion"}
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
